/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { QuizActivityTaskReq } from './quiz-activity-task-req'
import { QuizActivityTask } from './quiz-activity-task'
import { QuizReflectionBlockQuestion } from './quiz-reflection-block-question'
import { QuizBlockQuestion } from './quiz-block-question'
import { QuizTaskCompletedReq } from './quiz-task-completed-req'
import { TaskReflection } from './task-reflection'
import { QuizTaskReviewedReq } from './quiz-task-reviewed-req'
import { UpScreenReq } from './up-screen-req'
import { ReflectionAnswer } from './reflection-answer'
import { LoadQuizActivityTaskQuery } from './load-quiz-activity-task-query'
import { LoadQuizReflectionBlocksQuery } from './load-quiz-reflection-blocks-query'
import { QueryQuizTaskReflectionsQuery } from './query-quiz-task-reflections-query'
import { ExportQuizTaskReflectionsQuery } from './export-quiz-task-reflections-query'
import { QueryReviewedReflectionsQuery } from './query-reviewed-reflections-query'
import { QueryReflectionCompletedQuery } from './query-reflection-completed-query'

const buildConfig = createConfigBuilder('growp')

export const quizApi = {
  createQuizActivityTaskConfig(
    quizActivityTaskReq: QuizActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/quiz/activity-tasks',
      {},
      null,
      quizActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          quiz: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】测验-创建活动任务
   *  - 关联试卷，根据用户选择拷贝试卷
   * @param quizActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<QuizActivityTask>
   */
  createQuizActivityTask(
    quizActivityTaskReq: QuizActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuizActivityTask> {
    return axios(
      quizApi.createQuizActivityTaskConfig(quizActivityTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuizActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateQuizActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: QuizActivityTaskReq) => quizApi.createQuizActivityTask(payload),
      mutationOptions
    )
  },

  loadQuizActivityTaskConfig(
    id: number,
    loadQuizActivityTaskQuery?: LoadQuizActivityTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}',
      {
        id,
      },
      loadQuizActivityTaskQuery
    )
  },

  /**
   * @summary 【前/后台】测验-查询活动任务
   *  - 后台：活动任务+测验+是否有用户参与活动+是否有用户答题
   *  - 前台：活动任务+测验+活动任务完成情况
   * @param id
   * @param [loadQuizActivityTaskQuery]
   * @param [loadQuizActivityTaskQuery.expand] expand
   *  - 活动任务关联的活动人员 task.task_reflections
   *  - 活动任务关联的管理人员 task.admin_reflections
   * @param [loadQuizActivityTaskQuery.isForeground] 是否是前台请求
   *  - 是：返回-活动任务完成情况
   *  - 否：不返回-活动任务完成情况
   * @param [options]
   * @return AxiosResponsePromise<QuizActivityTask>
   */
  loadQuizActivityTask<TQuery extends LoadQuizActivityTaskQuery>(
    id: number,
    loadQuizActivityTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<QuizActivityTask, TQuery['expand']>> {
    return axios(
      quizApi.loadQuizActivityTaskConfig(id, loadQuizActivityTaskQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuizActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadQuizActivityTask<TQuery extends LoadQuizActivityTaskQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<QuizActivityTask, TQuery['expand']>> {
    return useQueryApi<Expand<QuizActivityTask, TQuery['expand']>>(
      quizApi.loadQuizActivityTask,
      'Quiz.loadQuizActivityTask',
      queryOptions,
      id,
      query
    )
  },

  updateQuizActivityTaskConfig(
    id: number,
    quizActivityTaskReq: QuizActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/quiz/activity-tasks/{id}',
      {
        id,
      },
      null,
      quizActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          task: (v: any) => v || null,
          quiz: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】测验-编辑活动任务
   *  - 有用户参与活动，不允许改 [1.测验时长 2.反作弊 3.开启方式]
   *  - 有用户提交数据，修改以下内容将清除当前用户提交数据 [1.测验模式 2.关联试卷 3.出题数量 4.通过得分率 ]
   *  - 修改关联试卷，根据用户选择，如有修改，基于选中试卷拷贝生成新试卷
   * @param id
   * @param quizActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<QuizActivityTask>
   */
  updateQuizActivityTask(
    id: number,
    quizActivityTaskReq: QuizActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuizActivityTask> {
    return axios(
      quizApi.updateQuizActivityTaskConfig(id, quizActivityTaskReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuizActivityTask(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateQuizActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: QuizActivityTaskReq }) =>
        quizApi.updateQuizActivityTask(id, payload),
      mutationOptions
    )
  },

  deleteQuizActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/quiz/activity-tasks/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】测验-删除活动任务
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteQuizActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(quizApi.deleteQuizActivityTaskConfig(id, options)) as any
  },

  useDeleteQuizActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => quizApi.deleteQuizActivityTask(id),
      mutationOptions
    )
  },

  loadQuizReflectionBlocksConfig(
    id: number,
    loadQuizReflectionBlocksQuery?: LoadQuizReflectionBlocksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}/blocks',
      {
        id,
      },
      loadQuizReflectionBlocksQuery
    )
  },

  /**
   * @summary 【前台】测验-根据测验类型为当前答题用户生成题目块
   *  - 普通模式 1. 生成唯一一条记录 2. 每次检查对应试卷题目块是否变化，并更新唯一记录
   *  - 抽题模式 1. 生成多条记录 2. 每次根据当前用户对本次题目作答是否被批阅为依据进行数据创建：未被批阅不生成数据；被批阅生成新数据
   * @param id
   * @param [loadQuizReflectionBlocksQuery]
   * @param [loadQuizReflectionBlocksQuery.isForeground] 是否是前台请求
   *  - 是：返回-活动任务完成情况
   *  - 否：不返回-活动任务完成情况
   *  - 前台根据token查结果
   *  - 后台根据rid查结果
   * @param [loadQuizReflectionBlocksQuery.newBlock] 抽题模式是否重新获取题目
   * @param [loadQuizReflectionBlocksQuery.rid]
   * @param [loadQuizReflectionBlocksQuery.reflectionDomain]
   * @param [options]
   * @return AxiosResponsePromise<QuizReflectionBlockQuestion>
   */
  loadQuizReflectionBlocks(
    id: number,
    loadQuizReflectionBlocksQuery?: LoadQuizReflectionBlocksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuizReflectionBlockQuestion> {
    return axios(
      quizApi.loadQuizReflectionBlocksConfig(
        id,
        loadQuizReflectionBlocksQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuizReflectionBlockQuestion(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadQuizReflectionBlocks(
    {
      id,
      query,
    }: {
      id: number
      query?: LoadQuizReflectionBlocksQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<QuizReflectionBlockQuestion> {
    return useQueryApi<QuizReflectionBlockQuestion>(
      quizApi.loadQuizReflectionBlocks,
      'Quiz.loadQuizReflectionBlocks',
      queryOptions,
      id,
      query
    )
  },

  loadQuizBlocksConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}/blocks/view',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】测验-测验预览 返回试卷下所有题目
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<QuizBlockQuestion>
   */
  loadQuizBlocks(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuizBlockQuestion> {
    return axios(quizApi.loadQuizBlocksConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new QuizBlockQuestion(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadQuizBlocks(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<QuizBlockQuestion> {
    return useQueryApi<QuizBlockQuestion>(
      quizApi.loadQuizBlocks,
      'Quiz.loadQuizBlocks',
      queryOptions,
      id
    )
  },

  checkQuizActivityTaskCompletedInfoConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/quiz/activity-tasks/{id}/check', {
      id,
    })
  },

  /**
   * @summary 【后台】测验-查询已完成活动信息
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  checkQuizActivityTaskCompletedInfo(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      quizApi.checkQuizActivityTaskCompletedInfoConfig(id, options)
    ) as any
  },

  useCheckQuizActivityTaskCompletedInfo(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<number> {
    return useQueryApi<number>(
      quizApi.checkQuizActivityTaskCompletedInfo,
      'Quiz.checkQuizActivityTaskCompletedInfo',
      queryOptions,
      id
    )
  },

  modifyQuizTaskCompletedConfig(
    id: number,
    quizTaskCompletedReq: QuizTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/quiz/activity-tasks/{id}/completed',
      {
        id,
      },
      null,
      quizTaskCompletedReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          startAt: (v: any) => v || null,
          endAt: (v: any) => v || null,
          duration: (v: any) => v || 0,
          answers: (v: any) => v || [],
          skip: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 【前台】测验-完成测验任务
   *    - 如用户不在测验任务人员列表内，将报错
   * @param id
   * @param quizTaskCompletedReq
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection>
   */
  modifyQuizTaskCompleted(
    id: number,
    quizTaskCompletedReq: QuizTaskCompletedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<TaskReflection> {
    return axios(
      quizApi.modifyQuizTaskCompletedConfig(id, quizTaskCompletedReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new TaskReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useModifyQuizTaskCompleted(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: QuizTaskCompletedReq }) =>
        quizApi.modifyQuizTaskCompleted(id, payload),
      mutationOptions
    )
  },

  queryQuizTaskReflectionsConfig(
    id: number,
    queryQuizTaskReflectionsQuery?: QueryQuizTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryQuizTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】测验任务-测验数据-任务人员列表(完成情况)
   * @param id
   * @param [queryQuizTaskReflectionsQuery]
   * @param [queryQuizTaskReflectionsQuery.expand]
   * @param [queryQuizTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryQuizTaskReflectionsQuery.reflection.nameIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.nameLike]
   * @param [queryQuizTaskReflectionsQuery.reflection.accountLike]
   * @param [queryQuizTaskReflectionsQuery.reflection.accountIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.genderIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.statusIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.phoneIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.phoneLike]
   * @param [queryQuizTaskReflectionsQuery.reflection.emailIn]
   * @param [queryQuizTaskReflectionsQuery.reflection.emailLike]
   * @param [queryQuizTaskReflectionsQuery.sort]
   * @param [queryQuizTaskReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryQuizTaskReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryQuizTaskReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [queryQuizTaskReflectionsQuery.startAtEgt] 测验任务-开始时间-大于
   * @param [queryQuizTaskReflectionsQuery.startAtElt] 测验任务-开始时间-小于
   * @param [queryQuizTaskReflectionsQuery.endAtEgt] 测验任务-完成时间-大于
   * @param [queryQuizTaskReflectionsQuery.endAtElt] 测验任务-完成时间-小于
   * @param [queryQuizTaskReflectionsQuery.gainedScoreEgt] 测验任务-得分-大于
   * @param [queryQuizTaskReflectionsQuery.gainedScoreElt] 测验任务-得分-小于
   * @param [queryQuizTaskReflectionsQuery.gainedScoreRateEgt] 测验任务-得分率-大于
   * @param [queryQuizTaskReflectionsQuery.gainedScoreRateElt] 测验任务-得分率-小于
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryQuizTaskReflections<TQuery extends QueryQuizTaskReflectionsQuery>(
    id: number,
    queryQuizTaskReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      quizApi.queryQuizTaskReflectionsConfig(
        id,
        queryQuizTaskReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryQuizTaskReflections<TQuery extends QueryQuizTaskReflectionsQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      quizApi.queryQuizTaskReflections,
      'Quiz.queryQuizTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  exportQuizTaskReflectionsConfig(
    id: number,
    exportQuizTaskReflectionsQuery?: ExportQuizTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}/reflections/export',
      {
        id,
      },
      exportQuizTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】测验任务-测验数据-任务人员列表(完成情况)
   * @param id
   * @param [exportQuizTaskReflectionsQuery]
   * @param [exportQuizTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [exportQuizTaskReflectionsQuery.reflection.nameIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.nameLike]
   * @param [exportQuizTaskReflectionsQuery.reflection.accountLike]
   * @param [exportQuizTaskReflectionsQuery.reflection.accountIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.genderIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.statusIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.phoneIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.phoneLike]
   * @param [exportQuizTaskReflectionsQuery.reflection.emailIn]
   * @param [exportQuizTaskReflectionsQuery.reflection.emailLike]
   * @param [exportQuizTaskReflectionsQuery.filename] 不带后缀文件名
   * @param [exportQuizTaskReflectionsQuery.ridIn]
   * @param [exportQuizTaskReflectionsQuery.startAtEgt] 测验任务-开始时间-大于
   * @param [exportQuizTaskReflectionsQuery.startAtElt] 测验任务-开始时间-小于
   * @param [exportQuizTaskReflectionsQuery.endAtEgt] 测验任务-完成时间-大于
   * @param [exportQuizTaskReflectionsQuery.endAtElt] 测验任务-完成时间-小于
   * @param [exportQuizTaskReflectionsQuery.gainedScoreEgt] 测验任务-得分-大于
   * @param [exportQuizTaskReflectionsQuery.gainedScoreElt] 测验任务-得分-小于
   * @param [exportQuizTaskReflectionsQuery.gainedScoreRateEgt] 测验任务-得分率-大于
   * @param [exportQuizTaskReflectionsQuery.gainedScoreRateElt] 测验任务-得分率-小于
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  exportQuizTaskReflections(
    id: number,
    exportQuizTaskReflectionsQuery?: ExportQuizTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      quizApi.exportQuizTaskReflectionsConfig(
        id,
        exportQuizTaskReflectionsQuery,
        options
      )
    ) as any
  },

  useExportQuizTaskReflections(
    {
      id,
      query,
    }: {
      id: number
      query?: ExportQuizTaskReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      quizApi.exportQuizTaskReflections,
      'Quiz.exportQuizTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  modifyTaskReviewedConfig(
    id: number,
    quizTaskReviewedReq: QuizTaskReviewedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/quiz/activity-tasks/{id}/reviewed',
      {
        id,
      },
      null,
      quizTaskReviewedReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】把测验任务状态改为已批阅
   * @param id
   * @param quizTaskReviewedReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  modifyTaskReviewed(
    id: number,
    quizTaskReviewedReq: QuizTaskReviewedReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      quizApi.modifyTaskReviewedConfig(id, quizTaskReviewedReq, options)
    ) as any
  },

  useModifyTaskReviewed(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: QuizTaskReviewedReq }) =>
        quizApi.modifyTaskReviewed(id, payload),
      mutationOptions
    )
  },

  queryReviewedReflectionsConfig(
    id: number,
    queryReviewedReflectionsQuery?: QueryReviewedReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/activity-tasks/{id}/reviewed-reflections',
      {
        id,
      },
      queryReviewedReflectionsQuery
    )
  },

  /**
   * @summary 【后台】测验任务-测验数据-查询任务批阅人员列表
   * @param id
   * @param [queryReviewedReflectionsQuery]
   * @param [queryReviewedReflectionsQuery.reviewedStatus] 根据用户批阅状态筛选数据
   * @param [queryReviewedReflectionsQuery.expand]
   * @param [queryReviewedReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryReviewedReflectionsQuery.reflection.nameIn]
   * @param [queryReviewedReflectionsQuery.reflection.nameLike]
   * @param [queryReviewedReflectionsQuery.reflection.accountLike]
   * @param [queryReviewedReflectionsQuery.reflection.accountIn]
   * @param [queryReviewedReflectionsQuery.reflection.genderIn]
   * @param [queryReviewedReflectionsQuery.reflection.statusIn]
   * @param [queryReviewedReflectionsQuery.reflection.phoneIn]
   * @param [queryReviewedReflectionsQuery.reflection.phoneLike]
   * @param [queryReviewedReflectionsQuery.reflection.emailIn]
   * @param [queryReviewedReflectionsQuery.reflection.emailLike]
   * @param [queryReviewedReflectionsQuery.sort]
   * @param [queryReviewedReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryReviewedReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryReviewedReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryReviewedReflections<TQuery extends QueryReviewedReflectionsQuery>(
    id: number,
    queryReviewedReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      quizApi.queryReviewedReflectionsConfig(
        id,
        queryReviewedReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryReviewedReflections<TQuery extends QueryReviewedReflectionsQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      quizApi.queryReviewedReflections,
      'Quiz.queryReviewedReflections',
      queryOptions,
      id,
      query
    )
  },

  upScreenInfoConfig(
    id: number,
    upScreenReq: UpScreenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/quiz/activity-tasks/{id}/screen',
      {
        id,
      },
      null,
      upScreenReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          completedDetails: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【前台】测验-上传截屏信息
   *    - 如用户不在测验任务人员列表内，将报错
   * @param id
   * @param upScreenReq
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection>
   */
  upScreenInfo(
    id: number,
    upScreenReq: UpScreenReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<TaskReflection> {
    return axios(quizApi.upScreenInfoConfig(id, upScreenReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new TaskReflection(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpScreenInfo(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: UpScreenReq }) =>
        quizApi.upScreenInfo(id, payload),
      mutationOptions
    )
  },

  queryReflectionCompletedConfig(
    activityTaskId: number,
    queryReflectionCompletedQuery?: QueryReflectionCompletedQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/quiz/completed',
      {
        activityTaskId,
      },
      queryReflectionCompletedQuery
    )
  },

  /**
   * @summary 【前/后台】测验-查询提交的答案
   *  - 后台：根据提交人id查询提交答案
   *  - 前台：根据token解析的id查询提交答案
   * @param activityTaskId
   * @param [queryReflectionCompletedQuery]
   * @param [queryReflectionCompletedQuery.isForeground] 是否是前台请求
   * 前台根据token查结果
   * 后台根据rid查结果
   * @param [queryReflectionCompletedQuery.rid]
   * @param [options]
   * @return AxiosResponsePromise<ReflectionAnswer>
   */
  queryReflectionCompleted(
    activityTaskId: number,
    queryReflectionCompletedQuery?: QueryReflectionCompletedQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ReflectionAnswer> {
    return axios(
      quizApi.queryReflectionCompletedConfig(
        activityTaskId,
        queryReflectionCompletedQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new ReflectionAnswer(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryReflectionCompleted(
    {
      activityTaskId,
      query,
    }: {
      activityTaskId: number
      query?: QueryReflectionCompletedQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<ReflectionAnswer> {
    return useQueryApi<ReflectionAnswer>(
      quizApi.queryReflectionCompleted,
      'Quiz.queryReflectionCompleted',
      queryOptions,
      activityTaskId,
      query
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(quizApi).forEach((api) => {
  // @ts-ignore
  quizApi[api]._name_ = `Quiz.${api}`
})
